/**
 * Sprint Screenshots Tab Content Component
 *
 * Displays all screenshots for a sprint in table view with View/Delete actions.
 * Shows source (Manual vs Agent), description, and upload metadata.
 *
 * Module: Sprint Management (SPRINT-2 Enhancement - Phase 2)
 * PRD: docs/sprints/SPRINT-2/sprint-module-enhancement-handoff.json
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, getAuthHeaders } from "@/lib/queryClient";
import type { SprintAttachment } from "@shared/schema";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Trash2, ImageIcon, Upload, Bot, Link2, User } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { ScreenshotUploadField } from "@/components/testing/screenshot-upload-field";

interface ScreenshotsTabContentProps {
  sprintId: string;
  screenshots: SprintAttachment[];
  isLoading?: boolean;
}

const SOURCE_CONFIG = {
  manual_upload: {
    label: "Manual",
    variant: "default" as const,
    icon: User,
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  },
  design_review_agent: {
    label: "Design Review",
    variant: "secondary" as const,
    icon: Bot,
    color: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  },
  qa_review_agent: {
    label: "QA Review",
    variant: "secondary" as const,
    icon: Bot,
    color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  },
  playwright_mcp: {
    label: "Playwright",
    variant: "outline" as const,
    icon: Link2,
    color: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  },
  bulk_import: {
    label: "Imported",
    variant: "outline" as const,
    icon: Upload,
    color: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  },
};

export function ScreenshotsTabContent({
  sprintId,
  screenshots,
  isLoading,
}: ScreenshotsTabContentProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = useState<SprintAttachment | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async (screenshotId: string) => {
      const res = await apiRequest("DELETE", `/api/project-manager/sprints/${sprintId}/screenshots/${screenshotId}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/project-manager/sprints", sprintId, "screenshots"] });
      toast({
        title: "Screenshot deleted",
        description: "The screenshot has been removed from this sprint.",
      });
      setDeleteConfirmOpen(false);
      setDeletingId(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete screenshot",
        variant: "destructive",
      });
    },
  });

  const handleView = (screenshot: SprintAttachment) => {
    setSelectedScreenshot(screenshot);
    setLightboxOpen(true);
  };

  const handleDeleteClick = (screenshotId: string) => {
    setDeletingId(screenshotId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deletingId) {
      deleteMutation.mutate(deletingId);
    }
  };

  const handleUploadClick = () => {
    setUploadDialogOpen(true);
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("captureType", "manual_upload");
      formData.append("description", "Screenshot uploaded from Screenshots tab");

      const authHeaders = await getAuthHeaders();
      const response = await fetch(`/api/project-manager/sprints/${sprintId}/screenshots/upload`, {
        method: "POST",
        headers: authHeaders,
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload screenshot");
      }

      queryClient.invalidateQueries({ queryKey: ["/api/project-manager/sprints", sprintId, "screenshots"] });

      toast({
        title: "Screenshot uploaded",
        description: "The screenshot has been added to this sprint.",
      });

      setUploadFile(null);
      setUploadDialogOpen(false);
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload screenshot",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const deletingScreenshot = screenshots.find((s) => s.id === deletingId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-muted-foreground">Loading screenshots...</p>
      </div>
    );
  }

  // Empty state rendering - must still include dialogs
  if (screenshots.length === 0) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-16">
          <ImageIcon className="h-16 w-16 text-muted-foreground mb-4" />
          <p className="text-lg font-medium mb-2">No screenshots attached</p>
          <p className="text-sm text-muted-foreground mb-6">
            Upload screenshots from visual testing or manually add evidence.
          </p>
          <Button onClick={handleUploadClick}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Screenshot
          </Button>
        </div>

        {/* Upload Screenshot Dialog - Required for empty state upload */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent className="sm:max-w-[500px]" aria-describedby="upload-screenshot-description-empty">
            <DialogTitle>Upload Screenshot</DialogTitle>
            <DialogDescription id="upload-screenshot-description-empty">
              Add a screenshot to provide visual evidence for this sprint.
            </DialogDescription>

            <div className="space-y-4 py-4">
              <ScreenshotUploadField
                value={uploadFile}
                onChange={(files) => setUploadFile(Array.isArray(files) ? files[0] ?? null : files)}
                label="Screenshot"
                required={true}
                disabled={isUploading}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setUploadDialogOpen(false);
                  setUploadFile(null);
                }}
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button onClick={handleUploadSubmit} disabled={!uploadFile || isUploading}>
                {isUploading ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Header with Upload Button */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">All Screenshots ({screenshots.length})</h3>
          <Button onClick={handleUploadClick} size="sm">
            <Upload className="h-4 w-4 mr-2" />
            Upload Screenshot
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-[80px]">Preview</TableHead>
                <TableHead className="w-[200px]">Filename</TableHead>
                <TableHead className="w-[140px]">Source</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[140px]">Uploaded</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {screenshots.map((screenshot) => {
                const sourceConfig = SOURCE_CONFIG[screenshot.source] || SOURCE_CONFIG.manual_upload;
                const SourceIcon = sourceConfig.icon;

                return (
                  <TableRow key={screenshot.id} className="hover:bg-muted/50">
                    {/* Thumbnail */}
                    <TableCell>
                      <div
                        className="w-12 h-12 rounded overflow-hidden border cursor-pointer hover:ring-2 hover:ring-primary"
                        onClick={() => handleView(screenshot)}
                      >
                        <img
                          src={`/api/project-manager/sprints/${sprintId}/screenshots/${screenshot.id}/download`}
                          alt={screenshot.fileName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = '/placeholder-image.png';
                          }}
                        />
                      </div>
                    </TableCell>

                    {/* Filename */}
                    <TableCell>
                      <button
                        onClick={() => handleView(screenshot)}
                        className="text-sm font-medium hover:underline text-left truncate max-w-[180px] block"
                        title={screenshot.fileName}
                      >
                        {screenshot.fileName}
                      </button>
                      <p className="text-xs text-muted-foreground">
                        {(screenshot.fileSize / 1024).toFixed(1)} KB
                      </p>
                    </TableCell>

                    {/* Source */}
                    <TableCell>
                      <Badge variant={sourceConfig.variant} className={sourceConfig.color}>
                        <SourceIcon className="h-3 w-3 mr-1" />
                        {sourceConfig.label}
                      </Badge>
                      {screenshot.uploadedByAgent && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {screenshot.uploadedByAgent}
                        </p>
                      )}
                    </TableCell>

                    {/* Description */}
                    <TableCell>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {screenshot.description || "—"}
                      </p>
                    </TableCell>

                    {/* Uploaded Date */}
                    <TableCell>
                      <p className="text-sm">
                        {format(new Date(screenshot.createdAt), "MMM d, yyyy")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(screenshot.createdAt), "h:mm a")}
                      </p>
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleView(screenshot)}
                          title="View screenshot"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(screenshot.id)}
                          title="Delete screenshot"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl" aria-describedby="lightbox-description">
          <DialogTitle>{selectedScreenshot?.fileName || "Screenshot"}</DialogTitle>
          <DialogDescription id="lightbox-description">
            View screenshot in full size
          </DialogDescription>
          {selectedScreenshot && (
            <div className="flex flex-col space-y-4">
              <img
                src={`/api/project-manager/sprints/${sprintId}/screenshots/${selectedScreenshot.id}/download`}
                alt={selectedScreenshot.fileName}
                className="w-full h-auto rounded-md border"
                onError={(e) => {
                  e.currentTarget.src = '/placeholder-image.png';
                }}
              />
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Source:</p>
                  <p className="font-medium">
                    {SOURCE_CONFIG[selectedScreenshot.source]?.label || selectedScreenshot.source}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Uploaded:</p>
                  <p className="font-medium">
                    {format(new Date(selectedScreenshot.createdAt), "MMM d, yyyy 'at' h:mm a")}
                  </p>
                </div>
                {selectedScreenshot.description && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground">Description:</p>
                    <p className="font-medium">{selectedScreenshot.description}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Screenshot Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" aria-describedby="upload-screenshot-description">
          <DialogTitle>Upload Screenshot</DialogTitle>
          <DialogDescription id="upload-screenshot-description">
            Add a screenshot to provide visual evidence for this sprint.
          </DialogDescription>

          <div className="space-y-4 py-4">
            <ScreenshotUploadField
              value={uploadFile}
              onChange={(files) => setUploadFile(Array.isArray(files) ? files[0] ?? null : files)}
              label="Screenshot"
              required={true}
              disabled={isUploading}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setUploadDialogOpen(false);
                setUploadFile(null);
              }}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button onClick={handleUploadSubmit} disabled={!uploadFile || isUploading}>
              {isUploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Screenshot?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingScreenshot?.fileName}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
